import { useEffect, useRef, useState } from 'react';

import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { daysToNextRenewal, getNextRenewalDate } from 'helpers/renewals-helper';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, ToggleSwitchInput } from 'components/FormComponents';
import { RenewalsSelector } from 'components/RenewalsSelector/RenewalsSelector';

export const EmployeeConfigurationModal = ({
  candidateChecks = [],
  renewalConfigs,
  companyChecks,
  visible,
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const renewalsSelectorRef = useRef(null);

  const [currentRenewalChecks, setCurrentRenewalChecks] = useState([]);
  const [renewalChecks, setRenewalChecks] = useState([]);
  const [nonRenewalChecks, setNonRenewalChecks] = useState([]);

  const [checkRenewalData, setCheckRenewalsData] = useState([]);

  useEffect(() => {
    if (!renewalConfigs || renewalConfigs.length < 1) return;

    let latestCandidateChecks = [];
    candidateChecks.forEach(candidateCheck => {
      const otherCandidateCheck = latestCandidateChecks.find(
        latestCandidateCheck => latestCandidateCheck.type === candidateCheck.type,
      );

      if (otherCandidateCheck && candidateCheck.completed_at > otherCandidateCheck.completed_at) {
        latestCandidateChecks.map(latestCandidateCheck => {
          if (latestCandidateCheck.type === candidateCheck.type) {
            return candidateCheck;
          } else {
            return latestCandidateCheck;
          }
        });
      } else if (!otherCandidateCheck) {
        latestCandidateChecks.push(candidateCheck);
      }
    });

    let currentRenewalChecks = [];
    let renewalChecks = [];
    let nonConfiguredChecks = [];

    latestCandidateChecks.forEach(candidateCheck => {
      const checkRenewalConfigs = renewalConfigs.filter(
        renewalConfig => renewalConfig.type === candidateCheck.type,
      );

      if (checkRenewalConfigs.length > 0) {
        checkRenewalConfigs.forEach(renewalConfig => {
          currentRenewalChecks.push({ renewalConfig, candidateCheck });
        });
      } else {
        const companyCheck = companyChecks.find(
          companyCheck => companyCheck.type === candidateCheck.type,
        );
        if (companyCheck) {
          nonConfiguredChecks.push({ checkType: companyCheck.check_type, candidateCheck });
        }
      }
    });

    renewalConfigs.forEach(renewalConfig => {
      if (
        currentRenewalChecks.findIndex(
          currentCheck => currentCheck.renewalConfig.type === renewalConfig.type,
        ) < 0
      ) {
        renewalChecks.push(renewalConfig);
      }
    });

    setCurrentRenewalChecks(currentRenewalChecks);
    setRenewalChecks(renewalChecks);
    setNonRenewalChecks(nonConfiguredChecks);
  }, [candidateChecks, renewalConfigs, companyChecks]);

  const handleSubmit = () => {
    const newRenewals = renewalsSelectorRef.current.getValues();
    onSubmit({
      renewal_configs: newRenewals,
      check_renewals: checkRenewalData,
    });
  };

  const onCheckToggle = ({ renewalConfig, candidateCheck, enabled }) => {
    if (!enabled) {
      setCheckRenewalsData(prev =>
        prev.filter(check => check.renewal_config_id !== renewalConfig.id),
      );
    } else {
      setCheckRenewalsData(prev => [
        ...prev,
        {
          candidate_check_id: candidateCheck.id,
          renewal_config_id: renewalConfig.id,
          type: renewalConfig.type,
          next_renew_at: getNextRenewalDate(renewalConfig, candidateCheck, 'YYYY-MM-DD'),
        },
      ]);
    }
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title="Add New Employee for Re-screening"
      description="Add all relevant employee data and select the bundle/checks that will need to be continuously monitored throughout their employment tenure. "
      small
      icon="check"
      noClose>
      <div className="card card-with-border u-padding">
        {currentRenewalChecks.length > 0 && (
          <>
            <h1 className="title-5 u-bold u-margin-bottom">
              Current checks with renewal configurations
            </h1>
            {currentRenewalChecks?.map(currentRenewalCheck => (
              <ToggleSwitchInput
                key={currentRenewalCheck.renewalConfig.id}
                text={currentRenewalCheck.renewalConfig.name}
                subtext={`Next renew at ${getNextRenewalDate(currentRenewalCheck.renewalConfig, currentRenewalCheck.candidateCheck)} (in ${daysToNextRenewal(currentRenewalCheck.renewalConfig, currentRenewalCheck.candidateCheck)} days)`}
                icon={getCheckTypeIcon(currentRenewalCheck.renewalConfig.type)}
                dataTestId={`toggle-switch-${currentRenewalCheck.renewalConfig.type}`}
                onChange={e => onCheckToggle({ ...currentRenewalCheck, enabled: e.target.checked })}
              />
            ))}
          </>
        )}

        {renewalChecks.length > 0 && (
          <>
            {currentRenewalChecks.length > 0 && <div className="u-divider" />}
            <h1 className="title-5 u-bold u-margin-bottom">
              Available check renewal configurations
            </h1>
            <RenewalsSelector
              ref={renewalsSelectorRef}
              renewalConfigs={renewalChecks}
              companyChecks={companyChecks}
            />
          </>
        )}

        {nonRenewalChecks.length > 0 && (
          <>
            <div className="u-divider" />
            <h1 className="title-5 u-bold u-margin-bottom">Checks without renewal configuration</h1>
            {nonRenewalChecks?.map(nonRenewalCheck => (
              <ToggleSwitchInput
                key={nonRenewalCheck.candidateCheck.id}
                text={nonRenewalCheck.checkType.title}
                subtext="This check has no renewal configuration"
                toggleReplacementButtonText="Add renewal configuration"
                toggleReplacementButtonType="secondary"
                onToggleReplacementButton={() => window.open('/renewals/configuration', '_blank')}
                logo={nonRenewalCheck.checkType.logo}
                icon={getCheckTypeIcon(nonRenewalCheck.checkType.slug)}
                dataTestId={`toggle-switch-${nonRenewalCheck.checkType.slug}`}
              />
            ))}
          </>
        )}
      </div>

      <div className="u-flex-box u-flex-justify-between u-margin-top--large">
        <Button onClick={onClose} type="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Apply Configuration</Button>
      </div>
    </AnimatedModal>
  );
};
